import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Menu,
  MenuItem,
  Backdrop
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import { useDispatch, useSelector } from "react-redux"
import {
  getGiftCardRedemptionsAction,
  changeRedemptionStatusAction
} from "redux/superAdmin/actions"
import AppPagination from "components/StyledComponents/AppPagination"
import TableNoRecords from "components/TableNoRecords"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import AppSelect from "components/StyledComponents/AppSelect"
import { format } from "date-fns"
import { useTheme } from "@mui/system"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { borderBottom: "none" }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { paddingRight: 0 }
  }
}))

const COLUMNS = [
  { id: "id", label: "S.No", minWidth: 60, align: "center" },
  { id: "Name", label: "Name", isSorting: true, sortKey: "user_name", minWidth: 150 },
  {
    id: "Email",
    label: "Email",
    isSorting: true,
    sortKey: "user_email",
    align: "center",
    minWidth: 180
  },
  {
    id: "Redeemed At",
    label: "Redeemed At",
    isSorting: true,
    sortKey: "redeemed_at",
    align: "center",
    minWidth: 160
  },
  {
    id: "Status",
    label: "Status",
    isSorting: true,
    sortKey: "status",
    align: "center",
    minWidth: 120
  },
  { id: "Action", label: "Action", align: "center", minWidth: 80 }
]

const statusMenuItems = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" }
]

const RedemptionsListModal = ({ open, onClose, giftCardId, rewardName }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { giftCardRedemptions } = useSelector((state) => state.superAdmin)
  const { userRestaurant } = useSelector((state) => state.user)
  const { isLoading } = useSelector((state) => state.app)

  const rows = Array.isArray(giftCardRedemptions)
    ? giftCardRedemptions
    : giftCardRedemptions?.results || []
  const totalCount = Array.isArray(giftCardRedemptions)
    ? giftCardRedemptions.length
    : giftCardRedemptions?.count || 0
  const ITEMS_PER_PAGE = 5
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  const [currentPage, setCurrentPage] = useState(1)

  // Filters and Sorting
  const [statusFilter, setStatusFilter] = useState("")
  const [order, setOrder] = useState("desc")
  const [sortKey, setSortKey] = useState("redeemed_at")

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const openMenu = Boolean(anchorEl)

  // Status Change Dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [targetStatus, setTargetStatus] = useState("")

  const loadData = () => {
    if (!giftCardId) return
    const params = {
      sort_by: sortKey,
      order: order
    }
    if (statusFilter) params.status = statusFilter

    dispatch(getGiftCardRedemptionsAction({ giftCardId, params, restaurantId: userRestaurant?.id }))
  }

  useEffect(() => {
    if (open) {
      loadData()
    } else {
      // Reset local state when closed
      setCurrentPage(1)
      setStatusFilter("")
      setSortKey("redeemed_at")
      setOrder("desc")
    }
  }, [open, statusFilter, sortKey, order, giftCardId])

  const paginatedRows = rows.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const handleSort = (key) => {
    const isSameKey = sortKey === key
    const newOrder = isSameKey && order === "desc" ? "asc" : "desc"
    setSortKey(key)
    setOrder(newOrder)
    setCurrentPage(1)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const onOptionsClick = (e, row) => {
    e.stopPropagation()
    setSelectedRow(row)
    setAnchorEl(e.currentTarget)
  }

  const promptStatusChange = (status) => {
    setTargetStatus(status)
    setConfirmDialogOpen(true)
    setAnchorEl(null)
  }

  const executeStatusChange = () => {
    if (!selectedRow?.id) return

    dispatch(
      changeRedemptionStatusAction({
        redemptionId: selectedRow.id,
        status: targetStatus,
        callback: () => {
          setConfirmDialogOpen(false)
          setSelectedRow(null)
          loadData()
        }
      })
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1C1A18",
          backgroundImage: "none",
          border: "1px solid rgba(173,145,111,0.2)",
          borderRadius: "16px",
          position: "relative",
          minHeight: "60vh"
        }
      }}>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 20,
          top: 20,
          color: "text.primary",
          backgroundColor: "#000",
          border: "1px solid rgba(255,255,255,0.1)",
          width: 28,
          height: 28,
          "&:hover": { backgroundColor: "#333" },
          zIndex: 10
        }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <DialogTitle sx={{ pt: 4, pb: 0 }}>
        <Typography
          variant="h3"
          sx={{
            fontFamily: "Orpheus Pro, serif",
            color: "text.primary",
            fontSize: "28px",
            fontWeight: 400
          }}>
          Redemptions: {rewardName}
        </Typography>
        <Typography variant="body2Regular" sx={{ color: "rgba(255,255,255,0.6)", mt: 1 }}>
          Total Redemptions: {totalCount}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 4, pt: 3 }}>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mb: 2, width: "300px", ml: "auto" }}>
          <AppSelect
            label="Filter by Status"
            value={statusFilter}
            menuItems={statusMenuItems}
            onMenuClick={(item) => {
              setStatusFilter(item.value)
              setCurrentPage(1)
            }}
          />
        </Box>

        <Paper sx={{ backgroundColor: "transparent", backgroundImage: "none", boxShadow: "none" }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <CustomTable stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column, index) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}>
                      <Typography variant="body2Regular" style={{ opacity: index === 0 ? 1 : 0.6 }}>
                        {column.label}{" "}
                        {column.isSorting && column.sortKey && (
                          <span
                            onClick={() => handleSort(column.sortKey)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              verticalAlign: "middle"
                            }}>
                            {sortKey === column.sortKey && order === "desc" ? (
                              <ArrowDownwardIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                            ) : sortKey === column.sortKey && order === "asc" ? (
                              <ArrowUpwardIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                            ) : (
                              <ArrowDownwardIcon
                                fontSize="inherit"
                                sx={{ ml: 0.5, opacity: 0.3 }}
                              />
                            )}
                          </span>
                        )}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length ? (
                  paginatedRows.map((row, index) => {
                    return (
                      <TableRow key={row.id}>
                        <TableCell align="center">
                          <Typography variant="body2Regular">
                            {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2Regular">{row.user?.name ?? "-"}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">{row.user?.email ?? "-"}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2Regular">
                            {row.redeemed_at
                              ? format(new Date(row.redeemed_at), "MMM d, yyyy h:mm a")
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            variant="filled"
                            color={
                              row.status?.toUpperCase() === "COMPLETED"
                                ? "success"
                                : row.status?.toUpperCase() === "CANCELLED"
                                ? "error"
                                : "warning"
                            }
                            label={row.status}
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {row.status?.toUpperCase() === "PENDING" ? (
                            <span onClick={(e) => onOptionsClick(e, row)}>
                              <MoreHorizIcon
                                sx={{ height: "24px", width: "24px", cursor: "pointer" }}
                              />
                            </span>
                          ) : (
                            <MoreHorizIcon sx={{ height: "24px", width: "24px", opacity: 0.3 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableNoRecords
                    colSpan={COLUMNS.length}
                    label={
                      isLoading
                        ? "Please Wait we are getting the redemptions..."
                        : "No redemptions found"
                    }
                  />
                )}
              </TableBody>
            </CustomTable>
          </TableContainer>

          <Backdrop
            sx={{ color: theme.palette.white.main, zIndex: 1300 }}
            open={openMenu}
            onClick={handleMenuClose}>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  marginTop: "15px",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "12px",
                  "& .MuiList-root": {
                    backgroundColor: theme.palette.background.default,
                    width: "150px",
                    boxSizing: "border-box",
                    padding: "10px 0"
                  }
                }
              }}>
              {selectedRow?.status === "PENDING" ? (
                <>
                  <StyledMenuItem onClick={() => promptStatusChange("COMPLETED")}>
                    <Typography variant="body1Regular" sx={{ color: theme.palette.green.main }}>
                      Approve
                    </Typography>
                  </StyledMenuItem>
                  <StyledMenuItem onClick={() => promptStatusChange("CANCELLED")}>
                    <Typography variant="body1Regular" sx={{ color: theme.palette.red.main }}>
                      Reject
                    </Typography>
                  </StyledMenuItem>
                </>
              ) : (
                <StyledMenuItem disabled>
                  <Typography variant="body1Regular" sx={{ opacity: 0.5 }}>
                    No Actions Available
                  </Typography>
                </StyledMenuItem>
              )}
            </Menu>
          </Backdrop>
        </Paper>

        {rows.length > 0 && totalPages > 1 && (
          <Box sx={{ mt: 3 }}>
            <AppPagination
              onPageChange={setCurrentPage}
              totalPages={totalPages}
              currentPage={currentPage}
            />
          </Box>
        )}
      </DialogContent>

      <DeleteConfirmationDialog
        open={confirmDialogOpen}
        title="Change Redemption Status"
        description={`Are you sure you want to mark this transaction as ${targetStatus}?`}
        btnText="Confirm"
        onClose={() => {
          setConfirmDialogOpen(false)
          setSelectedRow(null)
        }}
        onDelete={executeStatusChange}
      />
    </Dialog>
  )
}

export default RedemptionsListModal
